import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import {
    AppBar,
    Box,
    Button,
    Container,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

export default function Navbar() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    const navItems = [
        { name: 'Ana Sayfa', path: '/' },
        { name: 'Özellikler', path: '#features' },
        { name: 'Fiyatlandırma', path: '#pricing' },
        { name: 'Karşılaştırma', path: '#comparison' }
    ];

    const demoButton = { name: 'Demo Talep Et', path: '#demo-form', isDemoButton: true };

    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 50;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [scrolled]);

    // Tally script yükleme
    useEffect(() => {
        if (!document.querySelector('script[src="https://tally.so/widgets/embed.js"]')) {
            const script = document.createElement('script');
            script.src = 'https://tally.so/widgets/embed.js';
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setDrawerOpen(open);
    };

    const scrollToSection = (sectionId) => {
        setDrawerOpen(false);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const drawer = (
        <Box
            sx={{
                width: isSmallMobile ? '100vw' : 300,
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'white',
                position: 'fixed',
                top: 0,
                right: 0,
                zIndex: 2000,
                boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)',
                overflowY: 'auto',
            }}
            role="presentation"
            onClick={toggleDrawer(false)}
            onKeyDown={toggleDrawer(false)}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
                    <Box component="span" sx={{ color: theme.palette.secondary.main }}>
                        Trend
                    </Box>
                    <Box component="span" sx={{ color: theme.palette.primary.main }}>
                        Asist
                    </Box>
                </Typography>
                <IconButton onClick={toggleDrawer(false)}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <List sx={{ py: 2 }}>
                    {navItems.map((item, index) => (
                        <ListItem
                            button
                            key={index}
                            onClick={() => {
                                if (item.path.startsWith('#')) {
                                    scrollToSection(item.path.substring(1));
                                } else {
                                    window.location.href = item.path;
                                }
                            }}
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    bgcolor: 'rgba(255, 96, 0, 0.05)',
                                }
                            }}
                        >
                            <ListItemText
                                primary={item.name}
                                primaryTypographyProps={{
                                    fontWeight: 600,
                                    color: '#333333',
                                }}
                            />
                        </ListItem>
                    ))}

                    {/* Demo Talep Et Butonu - Mobil */}
                    <Box sx={{ px: 2, py: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            data-tally-open="wMV9kA"
                            data-tally-layout="modal"
                            data-tally-width="400"
                            data-tally-auto-close="0"
                            sx={{
                                py: 1.5,
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                borderRadius: '25px',
                                fontWeight: 700,
                                '&:hover': {
                                    bgcolor: theme.palette.primary.dark,
                                }
                            }}
                        >
                            {demoButton.name}
                        </Button>
                    </Box>
                </List>
            </Box>
            <Box sx={{ p: 2, borderTop: '1px solid #eee', flexShrink: 0 }}>
                <Button
                    variant="contained"
                    fullWidth
                    component="a"
                    href="/#/login"
                    sx={{
                        mb: 1.5,
                        borderRadius: '8px',
                        py: 1.5,
                        fontWeight: 600
                    }}
                >
                    Giriş Yap
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    component="a"
                    href="/#/register"
                    sx={{
                        borderRadius: '8px',
                        py: 1.5,
                        fontWeight: 600
                    }}
                >
                    Kayıt Ol
                </Button>
            </Box>
        </Box>
    );

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    boxShadow: scrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none',
                    bgcolor: 'white',
                    transition: 'all 0.3s',
                    height: scrolled ? (isMobile ? '60px' : '70px') : (isMobile ? '70px' : '80px'),
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 1100,
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ height: '100%' }}>
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <Typography
                                variant="h6"
                                component="a"
                                href="/"
                                sx={{
                                    fontWeight: 700,
                                    textDecoration: 'none',
                                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                                    mr: 2,
                                }}
                            >
                                <Box component="span" sx={{ color: theme.palette.secondary.main }}>
                                    Trend
                                </Box>
                                <Box component="span" sx={{ color: theme.palette.primary.main }}>
                                    Asist
                                </Box>
                            </Typography>
                        </motion.div>

                        <Box sx={{ flexGrow: 1 }} />

                        {!isMobile ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {navItems.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                    >
                                        <Button
                                            onClick={() => {
                                                if (item.path.startsWith('#')) {
                                                    scrollToSection(item.path.substring(1));
                                                }
                                            }}
                                            component={item.path.startsWith('#') ? 'button' : RouterLink}
                                            to={item.path.startsWith('#') ? undefined : item.path}
                                            sx={{
                                                mx: { sm: 0.5, md: 1 },
                                                color: '#333333',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    color: theme.palette.primary.main,
                                                    backgroundColor: 'transparent',
                                                },
                                                fontSize: { sm: '0.85rem', md: '0.9rem' }
                                            }}
                                        >
                                            {item.name}
                                        </Button>
                                    </motion.div>
                                ))}

                                {/* Demo Talep Et Butonu */}
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: navItems.length * 0.1 }}
                                >
                                    <Button
                                        data-tally-open="wMV9kA"
                                        data-tally-layout="modal"
                                        data-tally-width="400"
                                        data-tally-auto-close="0"
                                        sx={{
                                            mx: { sm: 0.5, md: 1 },
                                            color: 'white',
                                            fontWeight: 600,
                                            backgroundColor: theme.palette.primary.main,
                                            borderRadius: '25px',
                                            px: 2,
                                            py: 1,
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.dark,
                                            },
                                            fontSize: { sm: '0.85rem', md: '0.9rem' }
                                        }}
                                    >
                                        {demoButton.name}
                                    </Button>
                                </motion.div>

                                <Box sx={{ display: 'flex', ml: 2 }}>
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5, delay: navItems.length * 0.1 }}
                                    >
                                        <Button
                                            variant="text"
                                            component="a"
                                            href="/#/login"
                                            sx={{
                                                mr: 2,
                                                color: theme.palette.primary.main,
                                                '&:hover': {
                                                    color: theme.palette.primary.dark,
                                                    backgroundColor: 'transparent',
                                                },
                                                fontSize: { sm: '0.85rem', md: '0.9rem' }
                                            }}
                                        >
                                            Giriş Yap
                                        </Button>
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5, delay: (navItems.length + 1) * 0.1 }}
                                    >
                                        <Button
                                            variant="contained"
                                            component="a"
                                            href="/#/register"
                                            sx={{
                                                borderRadius: '8px',
                                                backgroundColor: theme.palette.primary.main,
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.dark,
                                                },
                                                fontSize: { sm: '0.85rem', md: '0.9rem' }
                                            }}
                                        >
                                            Kayıt Ol
                                        </Button>
                                    </motion.div>
                                </Box>
                            </Box>
                        ) : (
                            <IconButton
                                size="large"
                                edge="end"
                                color="inherit"
                                aria-label="menu"
                                onClick={toggleDrawer(true)}
                                sx={{
                                    color: theme.palette.primary.main,
                                    padding: '8px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 96, 0, 0.05)'
                                    }
                                }}
                            >
                                <MenuIcon fontSize="large" />
                            </IconButton>
                        )}
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
                sx={{
                    zIndex: 1500,
                    '& .MuiDrawer-paper': {
                        width: isSmallMobile ? '100%' : 300,
                        boxSizing: 'border-box',
                    },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
} 